// PageDescription: 실시간 의료 상담 리스트 페이지
import { QueryClient, dehydrate } from '@tanstack/react-query';
import { GetServerSideProps } from 'next';
import { NextSeo } from 'next-seo';
import React from 'react';

import DefaultLayout from '@/components/layouts/DefaultLayout';
import QnaRealtimeTemplate from '@/components/templates/mobile/communication/qna/qna-realtime-template';
import useSearchQuestions from '@/entities/qna/hook/useSearchQuestions';
import useSearchQuestionsInfinite from '@/entities/qna/hook/useSearchQuestionsInfinite';
import useUnifiedAdBanner from '@/hooks/advertisement/useUnifiedAdBanner';
import useQnaRealtimeViewModel from '@/hooks/qna/list/useQnaRealtimeViewModel';
import { useGetQnaCategoryGroupQuery } from '@/hooks/qna/mobile/qna.gql.impl';
import { AdPageType } from '@/models/ad/list';
import { CustomPageProps } from '@/types/app';
import { ROUTES } from '@/types/common';
import { withClientServerSideProps } from '@/utils/mobile/RNPlugins';
import { addQueryString } from '@/utils/url';

interface Props extends CustomPageProps {}

const QnaRealtime = ({ client }: Props) => {
  const viewModel = useQnaRealtimeViewModel();
  return (
    <>
      <NextSeo
        title='실시간 의료상담 - 닥터나우'
        description='24시간 대기하고 있는 닥터나우 의료인들이 실시간으로 답변해 드려요.'
        canonical={`${process.env.NEXT_PUBLIC_DOMAIN}${ROUTES.QNA.REALTIME.path}${addQueryString(viewModel.queries)}`}
        openGraph={{
          title: '실시간 의료상담 - 닥터나우',
          description: '24시간 대기하고 있는 닥터나우 의료인들이 실시간으로 답변해 드려요.',
          url: `${process.env.NEXT_PUBLIC_DOMAIN}${ROUTES.QNA.REALTIME.path}${addQueryString(viewModel.queries)}`,
          images: [
            {
              url: `${process.env.NEXT_PUBLIC_QNA_OG_IMAGE}`,
              alt: 'Og Image Alt',
            },
          ],
        }}
      />
      <QnaRealtimeTemplate client={client} viewModel={viewModel} />
    </>
  );
};

QnaRealtime.Layout = DefaultLayout;

export default QnaRealtime;

export const getServerSideProps: GetServerSideProps = withClientServerSideProps(async (ctx) => {
  const queryClient = new QueryClient();
  const categoryId = (ctx.query.categoryId as string) ?? '';
  const client = ctx.client;

  const page = (ctx.query.page as string) || '1';

  const NEWEST_QUESTION_SIZE = 20;

  const variables = {
    page: Number(page),
    size: NEWEST_QUESTION_SIZE,
    categoryIds: categoryId ? [categoryId] : [],
  };

  await Promise.allSettled([
    useSearchQuestions.prefetchTotalCountQuery(queryClient, {
      categoryIds: categoryId ? [categoryId] : [],
      size: variables.size,
    }),
    client.isWebView
      ? useSearchQuestionsInfinite.prefetchInfiniteQuery(queryClient, variables)
      : useSearchQuestions.prefetchItemsQuery(queryClient, variables),
    queryClient.prefetchQuery(useGetQnaCategoryGroupQuery.getKey(), useGetQnaCategoryGroupQuery.fetcher()),
    useUnifiedAdBanner.prefetchQuery(queryClient, AdPageType.QnaListBanner, ctx.client),
    useUnifiedAdBanner.prefetchQuery(queryClient, AdPageType.TopCommonBanner, ctx.client),
  ]);

  return {
    props: {
      dehydratedState: JSON.parse(JSON.stringify(dehydrate(queryClient))),
    },
  };
});
